import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import api from '../../api';

const DEFAULT_LIMIT = 50;

const labelOptions = [
  { value: 'bullish_reversal', label: 'Bullish Reversal' },
  { value: 'bearish_reversal', label: 'Bearish Reversal' },
  { value: 'not_reversal', label: 'Not a Reversal' },
];

const timeframeOptions = [
  '5minute',
  '15minute',
  '30minute',
  '60minute',
].map((v) => ({ value: v, label: v }));

function isoNowLocalToISO() {
  // datetime-local expects "YYYY-MM-DDTHH:mm"
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const ReversalCandleLabelerPage = () => {
  const [tab, setTab] = useState('create'); // create | browse

  // Create form
  const [symbol, setSymbol] = useState('NIFTY50');
  const [timeframe, setTimeframe] = useState('5minute');
  const [candleTimestamp, setCandleTimestamp] = useState(isoNowLocalToISO());
  const [label, setLabel] = useState('bearish_reversal');
  const [subtype, setSubtype] = useState('');
  const [notes, setNotes] = useState('');

  const [open, setOpen] = useState('');
  const [high, setHigh] = useState('');
  const [low, setLow] = useState('');
  const [close, setClose] = useState('');
  const [volume, setVolume] = useState('');

  const [ema7, setEma7] = useState('');
  const [ema9, setEma9] = useState('');
  const [ema20, setEma20] = useState('');
  const [rsi14, setRsi14] = useState('');
  const [atr, setAtr] = useState('');

  const [distPivot, setDistPivot] = useState('');
  const [distSupport, setDistSupport] = useState('');
  const [distResistance, setDistResistance] = useState('');

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');
  const [submitErr, setSubmitErr] = useState('');

  // Browse list
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [filterLabel, setFilterLabel] = useState('');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [filterTimeframe, setFilterTimeframe] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [listErr, setListErr] = useState('');

  const pages = useMemo(() => Math.max(1, Math.ceil((total || 0) / (limit || 1))), [total, limit]);
  const pageIndex = useMemo(() => Math.floor((offset || 0) / (limit || 1)) + 1, [offset, limit]);

  const fetchLabels = useCallback(async () => {
    setLoadingList(true);
    setListErr('');
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      if (filterLabel) params.set('label', filterLabel);
      if (filterSymbol) params.set('symbol', filterSymbol);
      if (filterTimeframe) params.set('timeframe', filterTimeframe);

      const resp = await api.get(`/api/v1/nifty/reversal-labels?${params.toString()}`);
      if (resp.data?.success) {
        setRows(resp.data.data || []);
        setTotal(resp.data.total || 0);
      } else {
        setRows(resp.data?.data || []);
        setTotal(resp.data?.total || 0);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to load labels';
      setListErr(msg);
    } finally {
      setLoadingList(false);
    }
  }, [filterLabel, filterSymbol, filterTimeframe, limit, offset]);

  useEffect(() => {
    if (tab !== 'browse') return;
    fetchLabels();
  }, [tab, fetchLabels]);

  const toNumOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitMsg('');
    setSubmitErr('');
    setSubmitLoading(true);
    try {
      const payload = {
        symbol: String(symbol || '').trim(),
        timeframe,
        candle_timestamp: new Date(candleTimestamp).toISOString(),
        label,
        subtype: subtype ? String(subtype).trim() : null,
        source: 'ui',
        notes: notes ? String(notes).trim() : null,

        open: Number(open),
        high: Number(high),
        low: Number(low),
        close: Number(close),
        volume: toNumOrNull(volume) ?? 0.0,

        ema_7: toNumOrNull(ema7),
        ema_9: toNumOrNull(ema9),
        ema_20: toNumOrNull(ema20),
        rsi_14: toNumOrNull(rsi14),
        atr: toNumOrNull(atr),

        dist_to_pivot: toNumOrNull(distPivot),
        dist_to_support: toNumOrNull(distSupport),
        dist_to_resistance: toNumOrNull(distResistance),
      };

      // Basic validation
      if (!payload.symbol) throw new Error('Symbol is required');
      if (!payload.timeframe) throw new Error('Timeframe is required');
      if (!payload.candle_timestamp) throw new Error('Candle timestamp is required');
      ['open', 'high', 'low', 'close'].forEach((k) => {
        if (!Number.isFinite(payload[k])) throw new Error(`Invalid ${k}`);
      });

      const resp = await api.post('/api/v1/nifty/reversal-labels', payload);
      if (!resp.data?.success) {
        throw new Error(resp.data?.detail || 'Failed to save label');
      }
      setSubmitMsg('Saved label successfully.');
      setSubmitErr('');

      // Optionally refresh browse list
      if (tab === 'browse') {
        fetchLabels();
      }
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || 'Failed to save label';
      setSubmitErr(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Reversal Candle Labeler</h1>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden">
            <button
              className={`px-4 py-2 text-sm font-medium ${tab === 'create' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              onClick={() => setTab('create')}
            >
              Create Label
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium ${tab === 'browse' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              onClick={() => setTab('browse')}
            >
              Browse Labels
            </button>
          </div>
        </div>

        <div className="mt-2 text-sm text-gray-600">
          This tool saves labeled candles to train the reversal detection model (Option A: OHLC features).
        </div>

        {tab === 'create' && (
          <form onSubmit={onSubmit} className="mt-6 bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-gray-600 mb-1">Symbol</div>
                <input className="w-full border rounded-lg px-3 py-2" value={symbol} onChange={(e) => setSymbol(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Timeframe</div>
                <select className="w-full border rounded-lg px-3 py-2" value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                  {timeframeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Candle Timestamp</div>
                <input
                  type="datetime-local"
                  className="w-full border rounded-lg px-3 py-2"
                  value={candleTimestamp}
                  onChange={(e) => setCandleTimestamp(e.target.value)}
                />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Label</div>
                <select className="w-full border rounded-lg px-3 py-2" value={label} onChange={(e) => setLabel(e.target.value)}>
                  {labelOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Subtype (optional)</div>
                <input className="w-full border rounded-lg px-3 py-2" value={subtype} onChange={(e) => setSubtype(e.target.value)} placeholder="hammer, engulfing..." />
              </div>
              <div className="md:col-span-3">
                <div className="text-xs text-gray-600 mb-1">Notes (optional)</div>
                <input className="w-full border rounded-lg px-3 py-2" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="why you labeled it..." />
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-600 mb-1">Open</div>
                <input className="w-full border rounded-lg px-3 py-2" value={open} onChange={(e) => setOpen(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">High</div>
                <input className="w-full border rounded-lg px-3 py-2" value={high} onChange={(e) => setHigh(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Low</div>
                <input className="w-full border rounded-lg px-3 py-2" value={low} onChange={(e) => setLow(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Close</div>
                <input className="w-full border rounded-lg px-3 py-2" value={close} onChange={(e) => setClose(e.target.value)} />
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Volume (optional)</div>
                <input className="w-full border rounded-lg px-3 py-2" value={volume} onChange={(e) => setVolume(e.target.value)} />
              </div>
            </div>

            <div className="mt-6">
              <div className="text-sm font-semibold text-gray-800 mb-2">Indicators (optional)</div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <div className="text-xs text-gray-600 mb-1">EMA 7</div>
                  <input className="w-full border rounded-lg px-3 py-2" value={ema7} onChange={(e) => setEma7(e.target.value)} />
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">EMA 9</div>
                  <input className="w-full border rounded-lg px-3 py-2" value={ema9} onChange={(e) => setEma9(e.target.value)} />
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">EMA 20</div>
                  <input className="w-full border rounded-lg px-3 py-2" value={ema20} onChange={(e) => setEma20(e.target.value)} />
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">RSI 14</div>
                  <input className="w-full border rounded-lg px-3 py-2" value={rsi14} onChange={(e) => setRsi14(e.target.value)} />
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">ATR (optional)</div>
                  <input className="w-full border rounded-lg px-3 py-2" value={atr} onChange={(e) => setAtr(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-sm font-semibold text-gray-800 mb-2">Distances (points, optional)</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Dist to Pivot</div>
                  <input className="w-full border rounded-lg px-3 py-2" value={distPivot} onChange={(e) => setDistPivot(e.target.value)} />
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Dist to Support</div>
                  <input className="w-full border rounded-lg px-3 py-2" value={distSupport} onChange={(e) => setDistSupport(e.target.value)} />
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Dist to Resistance</div>
                  <input className="w-full border rounded-lg px-3 py-2" value={distResistance} onChange={(e) => setDistResistance(e.target.value)} />
                </div>
              </div>
            </div>

            {(submitErr || submitMsg) && (
              <div className={`mt-4 p-3 rounded border text-sm ${submitErr ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
                {submitErr || submitMsg}
              </div>
            )}

            <div className="mt-5 flex items-center gap-3">
              <button
                type="submit"
                disabled={submitLoading}
                className={`px-5 py-2 rounded-lg font-medium text-white ${submitLoading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {submitLoading ? 'Saving…' : 'Save Label'}
              </button>
              <button
                type="button"
                className="px-5 py-2 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setSubtype('');
                  setNotes('');
                  setOpen('');
                  setHigh('');
                  setLow('');
                  setClose('');
                  setVolume('');
                  setEma7('');
                  setEma9('');
                  setEma20('');
                  setRsi14('');
                  setAtr('');
                  setDistPivot('');
                  setDistSupport('');
                  setDistResistance('');
                  setSubmitMsg('');
                  setSubmitErr('');
                }}
              >
                Clear
              </button>
            </div>
          </form>
        )}

        {tab === 'browse' && (
          <div className="mt-6 bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="flex-1">
                <div className="text-xs text-gray-600 mb-1">Label</div>
                <select className="w-full border rounded-lg px-3 py-2" value={filterLabel} onChange={(e) => { setOffset(0); setFilterLabel(e.target.value); }}>
                  <option value="">All</option>
                  {labelOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-600 mb-1">Symbol</div>
                <input className="w-full border rounded-lg px-3 py-2" value={filterSymbol} onChange={(e) => { setOffset(0); setFilterSymbol(e.target.value); }} placeholder="NIFTY50" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-600 mb-1">Timeframe</div>
                <select className="w-full border rounded-lg px-3 py-2" value={filterTimeframe} onChange={(e) => { setOffset(0); setFilterTimeframe(e.target.value); }}>
                  <option value="">All</option>
                  {timeframeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Rows</div>
                  <select className="border rounded-lg px-3 py-2" value={limit} onChange={(e) => { setOffset(0); setLimit(Number(e.target.value)); }}>
                    {[25, 50, 100, 200].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <button
                  className="h-10 mt-5 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  onClick={() => fetchLabels()}
                  disabled={loadingList}
                >
                  {loadingList ? 'Loading…' : 'Refresh'}
                </button>
              </div>
            </div>

            {listErr && (
              <div className="mt-4 p-3 rounded border bg-red-50 border-red-200 text-red-700 text-sm">
                {listErr}
              </div>
            )}

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-700 border-b">
                    <th className="py-2 pr-4">Time</th>
                    <th className="py-2 pr-4">Symbol</th>
                    <th className="py-2 pr-4">TF</th>
                    <th className="py-2 pr-4">Label</th>
                    <th className="py-2 pr-4">Subtype</th>
                    <th className="py-2 pr-4">OHLC</th>
                    <th className="py-2 pr-4">RSI</th>
                    <th className="py-2 pr-4">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b text-gray-800">
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {r.candle_timestamp ? new Date(r.candle_timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}
                      </td>
                      <td className="py-2 pr-4">{r.symbol}</td>
                      <td className="py-2 pr-4">{r.timeframe}</td>
                      <td className="py-2 pr-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          r.label === 'bullish_reversal' ? 'bg-green-100 text-green-700' :
                          r.label === 'bearish_reversal' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {r.label}
                        </span>
                      </td>
                      <td className="py-2 pr-4">{r.subtype || '—'}</td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {r.raw?.open}/{r.raw?.high}/{r.raw?.low}/{r.raw?.close}
                      </td>
                      <td className="py-2 pr-4">{r.features?.rsi_14 ?? r.raw?.rsi_14 ?? '—'}</td>
                      <td className="py-2 pr-4 whitespace-nowrap">
                        {r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}
                      </td>
                    </tr>
                  ))}
                  {!loadingList && rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-6 text-center text-gray-500">
                        No labels found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {rows.length} of {total} — Page {pageIndex} / {pages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-2 rounded border border-gray-300 text-gray-700 disabled:opacity-50"
                  disabled={offset === 0 || loadingList}
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                >
                  Prev
                </button>
                <button
                  className="px-3 py-2 rounded border border-gray-300 text-gray-700 disabled:opacity-50"
                  disabled={offset + limit >= total || loadingList}
                  onClick={() => setOffset(offset + limit)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ReversalCandleLabelerPage;

